import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Constants } from '../lib/const/constants';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { encryptPayload } from '../utils/encryption';
import {ArrowLeft, ArrowRight} from 'lucide-react'
import AddressSearchInput from '../components/shared/AddressSearchInput';

const S = {
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    width: '100%',
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    backgroundImage: 'linear-gradient(to bottom, #f8fafc 0%, #eff6ff4d 50%, #eef2ff66 100%)',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    paddingTop: '24px',
    paddingBottom: '24px',
    boxSizing: 'border-box',
    overflowX: 'hidden',
  },
  pageWrap: {
    width: '100%',
    maxWidth: '520px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  card: {
    background: '#fff',
    borderRadius: '16px',
    width: '100%',
    boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
  },
  header: {
    height: '66px',
    width: '100%',
    gap: '8px',
  },
  backDiv: {
    width: '100%',
    height: '48px',
    borderRadius: '16px',
    paddingTop: '12px',
    paddingBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    opacity: 1,
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#1D2338',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: '0',
    marginBottom: '16px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#394784',
    letterSpacing: '0px',
    lineHeight: '150%',
    textAlign: 'center',
  },
  pageSubtitle: {
    fontSize: '16px',
    fontWeight: '400',
    color: '#676F7D',
    lineHeight: '140%',
    letterSpacing: '0px',
    textAlign: 'center',
  },
  body: {
    width: '100%',
    paddingTop: '32px',
    paddingRight: '24px',
    paddingBottom: '24px',
    paddingLeft: '24px',
    borderRadius: '16px',
    border: '1px solid #F2F2F2',
    background: '#FFFFFF',
    boxShadow: '0px 4px 16px -4px rgba(0,0,0,0.06)',
    overflowY: 'visible',
    overflowX: 'visible',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    boxSizing: 'border-box',
  },
  sectionHeader: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#676F7D',
    letterSpacing: '0px',
    lineHeight: '100%',
    textTransform: 'uppercase',
    height: '9px',
  },
  inputWrap: {
    marginBottom: '1px',
  },
  input: {
    width: '100%',
    height: '56px',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #E2E6ED',
    background: '#F6F6F6',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '100%',
    color: '#000000',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  inputError: {
    borderColor: '#FCA5A5',
    background: '#FFF5F5',
  },
  errorText: {
    color: '#EF4444',
    fontSize: '12px',
    marginTop: '4px',
    paddingLeft: '2px',
  },
  selectWrap: {
    width: '100%',
    position: 'relative',
    overflow: 'visible',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    boxSizing: 'border-box',
  },
  selectTrigger: {
    width: '100%',
    height: '56px',
    borderRadius: '8px',
    border: '1px solid #E2E6ED',
    background: '#F6F6F6',
    padding: '16px',
    paddingRight: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '100%',
    color: '#8F959F',
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none',
    boxSizing: 'border-box',
  },
  chevronWrap: {
    position: 'absolute',
    right: '14px',
    top: '28px',
    transform: 'translateY(-50%)',
    color: '#9CA3AF',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
  },
  dropdown: {
    position: 'absolute',
    top: '60px',
    left: 0,
    right: 0,
    background: '#fff',
    border: '1px solid #EBEBEB',
    borderRadius: '10px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
    zIndex: 100,
    maxHeight: '220px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  dropdownSearch: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 14px',
    borderBottom: '1px solid #F5F5F5',
    gap: '8px',
    flexShrink: 0,
  },
  dropdownSearchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '13px',
    color: '#374151',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    background: 'transparent',
  },
  dropdownList: {
    overflowY: 'auto',
  },
  dropdownItem: {
    padding: '10px 14px',
    fontSize: '13.5px',
    color: '#374151',
    cursor: 'pointer',
  },
  radioToggleWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '24px',
  },
  radioOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    color: '#111827',
  },
  radioCircle: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '2px solid #C8CCDA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
  },
  radioInner: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: '#306ACE',
  },
  bankSelect: {
    width: '100%',
    padding: '13px 40px 13px 16px',
    background: '#F6F6F6',
    border: '1px solid #E2E6ED',
    borderRadius: '8px',
    color: '#374151',
    fontSize: '14px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    outline: 'none',
    appearance: 'none',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 14px center',
    cursor: 'pointer',
    boxSizing: 'border-box',
    marginBottom: '4px',
    height: '56px',
  },
  proceedBtn: {
    width: '100%',
    height: '56px',
    borderRadius: '12px',
    paddingTop: '12px',
    paddingRight: '20px',
    paddingBottom: '12px',
    paddingLeft: '20px',
    marginTop: '10px',
    background: '#D26521',
    boxShadow: '0px 8px 20px 0px #0000001F',
    color: '#fff',
    border: 'none',
    fontSize: '16px',
    fontWeight: 700,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    letterSpacing: '0.1px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    opacity: 1,
    transition: 'opacity 0.15s, transform 0.1s',
  },
  loadingWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '14px',
    color: '#6B7280',
    fontSize: '14px',
    marginTop: '16px',
  },
};

const Spinner = ({ color = '#E8863A', size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    fill="none"
    style={{ animation: 'ba-spin 0.8s linear infinite', flexShrink: 0 }}
  >
    <circle
      cx="9"
      cy="9"
      r="7"
      stroke={color}
      strokeWidth="2.5"
      strokeDasharray="32"
      strokeDashoffset="10"
      strokeLinecap="round"
    />
  </svg>
);
Spinner.propTypes = {
  color: PropTypes.string,
  size: PropTypes.number,
};

const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M3 5l4 4 4-4"
      stroke="#9CA3AF"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function BusinessAccount() {
  const navigate = useNavigate();
  const location = useLocation();
  const paramsdata = location.state;

  const node = paramsdata?.node;
  const Nav_node = paramsdata?.Nav_node;
  const currency = paramsdata?.currency;
  const transferflowamount = paramsdata?.transferflowamount;
  const currencyid = paramsdata?.currencyid;
  const balance = paramsdata?.balance;
  const routeName = paramsdata?.routeName;
  const node_location = paramsdata?.nodeType;
  const workspaces = JSON.parse(sessionStorage.getItem('login_workspaces'));
  const account_type = workspaces?.[0]?.type;
  const sendmoneynode = location.state?.sendmoneynode;
  const transfer_fee = paramsdata?.transfer_fee;
  const service_fee = paramsdata?.service_fee;
  const total_amount = paramsdata?.total_amount;

  const buyAmount = location.state?.buyAmount;
  const exchangeRate = location.state?.exchangeRate;
  const contactId = location.state?.contactId;
  const from_currency = location.state?.from_currency;
  const from_amount = location.state?.from_amount;
  const sendmoney_Nav_node = location.state?.sendmoney_Nav_node;
  const sbnode = location.state?.sbnode;

  const [loading, setLoading] = useState(false);
  const [beneType, setbeneType] = useState('regular');
  const [countries, setCountries] = useState([]);
  const [beneCountry, setbeneCountry] = useState('');
  const [formFeilds, setFormFeilds] = useState([]);
  const [formKeys, setFormKeys] = useState([]);
  const [bankDetails, setbankDetails] = useState({});
  const [errors, setErrors] = useState({});
  const [bank, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState();
  const [otherBank, setOtherBank] = useState();
  const [otherBankError, setOtherBankError] = useState('');
  const [bankError, setBankError] = useState('');
  const [searchtext, setSearchtext] = useState('');
  const [showdropdown, setShowdropdown] = useState(false);
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  const isemptyroute = routeName === '' || routeName === null;
  const [balances, setBalances] = useState([]);
  const [usedBalances, setusedBalances] = useState([]);
  const [inrPaymentType, setInrPaymentType] = useState('account_number');
  const isFromSendMoneyFlow = sendmoney_Nav_node;
  const staffLogin = sessionStorage.getItem('staff_login_id');
  const isStaffAllowed = (Constants.STAFF_LOGIN_ID_CONVERSION || []).includes(
    staffLogin,
  );

  useEffect(() => {
    if (!beneCountry || !beneType) return;
    getBeneFields(beneType, beneCountry);

    if (beneCountry === 'NG') {
      getNigeriaBanks();
    } else {
      setBanks([]);
      setSelectedBank('');
    }
  }, [beneCountry, beneType]);

  useEffect(() => {
    setOtherBank('');
    setOtherBankError('');
    setBankError('');
    setSelectedBank('');
  }, [beneCountry]);

  useEffect(() => {
    getData();
    getBalances();
    if (currencyid == 105) {
      getBeneFields('regular', 'IN');
      setbeneCountry('IN');
    }
  }, []);

  const filteredcountries = countries[0]
    ? countries[0].filter((c) =>
        c.name.toLowerCase().includes(searchtext.toLowerCase()),
      )
    : [];

  const getBalances = async () => {
    const login_workspaces_id = sessionStorage.getItem('login_workspaces_id');
    const login_token = sessionStorage.getItem('login_token');
    try {
      const resp = await axios.get(
        Constants.BASE_URL +
          'API-FX-231-GET-WALLET-BALANCES/' +
          login_workspaces_id,
        {
          headers: {
            Authorization: 'Bearer ' + JSON.parse(login_token),
            fx_key: Constants.SUBSCRIPTION_KEY,
          },
        },
      );
      const balArray = Object.values(resp.data);
      setusedBalances(resp.data.used_balances);
      balArray.pop();
      balArray.pop();
      balArray.pop();
      balArray.pop();
      if (balArray.length > 0) setBalances(balArray);
    } catch (err) {
      console.log(err.response?.data || err);
    }
  };

  const otpValidate = async (beneficiary_id) => {
    setLoading(true);
    const enteredOtp = Constants.OTP_VERIFICATION;
    const token = sessionStorage.getItem('login_token');

    try {
      const url =
        Constants.BASE_URL +
        'API-FX-144-beneficiaryverification/' +
        beneficiary_id;

      const encryptedPayload = await encryptPayload({ code: enteredOtp,platform: 'web',extraParamValidation: 'trustedfromweb' });
      const resp = await axios.post(
        url,
        encryptedPayload,
        {
          headers: {
            Authorization: 'Bearer ' + JSON.parse(token),
            fx_key: Constants.SUBSCRIPTION_KEY,
          },
        },
      );

      if (resp.data.code !== 400) {
        if (resp.data.message === 'Beneficiary OTP Verified.') {
          toast.success('OTP Verified Successfully');
          if (node_location === 'SendMoneyCurrency') {
            navigate('/SendMoneyCurrency', {
              state: { currency, transferflowamount, currencyid, balance },
            });
          } else if (sendmoneynode || sendmoney_Nav_node === 2) {
            navigate('/Choosepayment', {
              state: {
                sendmoneycurrency: currency,
                currencyid: currencyid,
                balance: currency
                  ? getAvailableBalance(currency).toLocaleString()
                  : '-',
                buyAmount: buyAmount,
                exchangeRate: exchangeRate,
                contactId: contactId,
                from_currency: from_currency,
                currency: currency,
                from_amount: from_amount,
                sendmoney_Nav_node: 2,
                transfer_fee:transfer_fee,
                service_fee:service_fee,
                total_amount:total_amount
              },
            });
          } else if (!routeName && account_type == 'business')
            navigate('/SendMoney', {
              state: {
                currency,
                transferflowamount,
                currencyid,
                balance,
                Nav_node: Nav_node,
              },
            });
          else if (!routeName && account_type == 'individual')
            navigate('/ListBeneficiaries', {
              state: {
                currency,
                transferflowamount,
                currencyid,
                balance,
                Nav_node: Nav_node,
              },
            });
          else
            navigate('/' + routeName, {
              state: { currency, transferflowamount, currencyid, balance },
            });
        } else {
          toast.error('Invalid OTP');
        }
      } else {
        toast.error(resp.data.message);
        navigate('/', { state: { qr_login_completed: 1 } });
      }
    } catch (err) {
      console.error(err.response?.data);
      toast.error('Invalid OTP');
    } finally {
      setLoading(false);
    }
  };
  const getAvailableBalance = (currencyCode) => {
    const balanceObj = balances.find((b) => b.currency === currencyCode);
    if (!balanceObj) return 0;
    const used = usedBalances[currencyCode] || 0;
    const available = parseFloat(balanceObj.balance) - parseFloat(used);
    return available < 0 ? 0 : available;
  };

  const submitHandler = async () => {
    let newErrors = {};
    let hasError = false;

    for (let i = 0; i < formFeilds.length; i++) {
      const field = formFeilds[i];
      const key = formKeys[i];
      if (
        currency.toLowerCase() === 'inr' &&
        inrPaymentType === 'upi' &&
        key === 'ifsc'
      )
        continue;
      if (field.is_required) {
        const value = (bankDetails[key] || '').toString();
        if (!value || value.trim() === '') {
          newErrors[key] = `${field.label} is required`;
          hasError = true;
          continue;
        }
        if (
          key === 'account_number' &&
          inrPaymentType === 'account_number' &&
          currency === 'GBP' &&
          (beneCountry === 'UK' || beneCountry === 'GB')
        ) {
          if (!/^\d+$/.test(value)) {
            newErrors[key] = 'Account Number must contain only digits';
            hasError = true;
          } else if (value.length !== 8) {
            newErrors[key] = 'Account Number must be 8 digits';
            hasError = true;
          }
        }
        if (
          key === 'sort_code' &&
          currency === 'GBP' &&
          (beneCountry === 'UK' || beneCountry === 'GB')
        ) {
          if (!/^\d+$/.test(value)) {
            newErrors[key] = 'Sort Code must contain only digits';
            hasError = true;
          } else if (value.length !== 6) {
            newErrors[key] = 'Sort Code must be 6 digits';
            hasError = true;
          }
        }
      }
    }

    const hasinput =
      bankDetails.iban ||
      bankDetails.bic_swift ||
      bankDetails.aba ||
      bankDetails.branch_code ||
      bankDetails.bsb_code ||
      bankDetails.routing_code ||
      (beneCountry === 'NG' && bankDetails.account_number);
    if (bank.length > 0 && hasinput && (!selectedBank || selectedBank === '')) {
      setBankError('Please select your bank.');
      hasError = true;
    } else {
      setBankError('');
    }
    if (hasinput && selectedBank === 'other') {
      if (!otherBank?.trim()) {
        setOtherBankError('Bank address is required');
        hasError = true;
      } else {
        setOtherBankError('');
      }
    } else {
      setOtherBankError('');
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setOtherBankError('');
    setLoading(true);

    const token = sessionStorage.getItem('login_token');
    const workspaceId = sessionStorage.getItem('login_workspaces_id');
    const defaultBank = sessionStorage.getItem('defaultBank');
    const clearBankCustomerId = sessionStorage.getItem('clearBankCustomerId');
    const workspace = sessionStorage.getItem('login_workspaces');

    var obj = {
      workspace_id: workspaceId,
      currency,
      type: 'company',
      beneficiary_first_name: bankDetails.beneficiary_first_name,
      beneficiary_last_name: bankDetails.beneficiary_last_name,
      payment_type: beneType,
      bank_country: beneCountry == 'UK' ? 'GB' : beneCountry,
      bic_swift: bankDetails.bic_swift,
      account_number: bankDetails.account_number ?? bankDetails.iban,
      iban: bankDetails.iban,
      routing_code: bankDetails.routing_code,
      ifsc: bankDetails.ifsc || '-',
      bank_code:
        beneCountry === 'NG' && selectedBank && selectedBank !== 'other'
          ? JSON.parse(selectedBank).branch_code
          : bankDetails.bank_code,
      branch_code: bankDetails.branch_code,
      cnaps: bankDetails.cnaps,
      bsb_code: bankDetails.bsb_code,
      clabe: bankDetails.clabe,
      sort_code: bankDetails.sort_code,
      institution_no: bankDetails.institution_no,
      aba: bankDetails.aba,
      bank_account_holder_name:
        bankDetails.beneficiary_first_name +
        ' ' +
        bankDetails.beneficiary_last_name,
      beneficiary_address: bankDetails.beneficiary_address,
      beneficiary_city: bankDetails.beneficiary_city,
      beneficiary_state_or_province: bankDetails.beneficiary_state_or_province,
      beneficiary_company_name: bankDetails.beneficiary_company_name,
      beneficiary_postcode: bankDetails.beneficiary_postcode,
      bank_details:
        selectedBank === 'other'
          ? otherBank
          : beneCountry === 'NG'
            ? selectedBank
              ? JSON.parse(selectedBank)
              : ''
            : selectedBank,
            platform: 'web',
              extraParamValidation: 'trustedfromweb'
    };

    if (workspace) {
      if (defaultBank == 'Clear Bank as Service') {
        console.log('cb');
      } else {
        const parsedWorkspace = JSON.parse(workspace);
        if (Object.keys(parsedWorkspace[0].accounts).length > 0) {
          console.log('cb');
        } else {
          console.log('wallet');
        }
      }
    }

    if (
      currencyid == 231 &&
      defaultBank == 'Clear Bank as Service' &&
      (beneCountry == 'UK' || beneCountry == 'GB')
    ) {
      obj = {
        workspace_id: workspaceId,
        type: 'company',
        first_name: bankDetails.beneficiary_first_name,
        middle_name: '',
        last_name: bankDetails.beneficiary_last_name,
        email: '',
        mobile: '7777777777',
        bank_account_number: bankDetails.account_number,
        sort_code: bankDetails.sort_code,
        bank_code_type: 'sort-code',
        bank_account_name: bankDetails.beneficiary_company_name,
        bank_country: 231,
        beneficiary_type: ['supplier', 'customer'],
        payment_type: 'regular',
        beneficiary_address: bankDetails.beneficiary_address,
        beneficiary_city: bankDetails.beneficiary_city,
        post_code: 'NANANA',
        beneficiary_state: 'NANANA',
        customer_id: clearBankCustomerId,
        platform: 'web',
        extraParamValidation: 'trustedfromweb'
      };
      const encryptedPayload = await encryptPayload(obj)
      await axios
        .post(
          Constants.BASE_URL + 'API-FX-190-CLEAR-BANK-CREATE-BENEFICIARY',
          encryptedPayload,
          {
            headers: {
              fx_key: Constants.SUBSCRIPTION_KEY,
              Authorization: 'Bearer ' + JSON.parse(token),
            },
          },
        )
        .then((resp) => {
          console.log(resp.data);
          toast.success('Beneficiary Added Successfully.');
          if (node_location === 'SendMoneyCurrency')
            navigate('/SendMoneyCurrency', {
              state: { currency, transferflowamount, currencyid, balance },
            });
          else if (isemptyroute && account_type === 'business')
            navigate('/SendMoney', {
              state: { currency, transferflowamount, currencyid, balance },
            });
          else if (isemptyroute && account_type === 'individual')
            navigate('/ListBeneficiaries', {
              state: { currency, transferflowamount, currencyid, balance },
            });
          else
            navigate('/' + routeName, {
              state: { currency, transferflowamount, currencyid, balance },
            });
          setLoading(false);
        })
        .catch((err) => {
          let rawMsg = err.response?.data?.message;
          let cleanMsg = rawMsg.match(/"detail":"([^"]+)"/)?.[1] || rawMsg;
          toast.error(cleanMsg);
          setLoading(false);
        });
    } else {
      const encryptedPayload = await encryptPayload(obj)
      await axios
        .post(Constants.BASE_URL + 'API-FX-234-CREATE-OWN-BENEFICIARY', encryptedPayload, {
          headers: {
            fx_key: Constants.SUBSCRIPTION_KEY,
            Authorization: 'Bearer ' + JSON.parse(token),
          },
        })
        .then((resp) => {
          // console.log(resp.data);
          const beneficiary_id = resp.data.beneficiary_id
          // console.log(beneficiary_id)
          setLoading(false);
          if(isStaffAllowed?otpValidate(beneficiary_id):
          navigate('/BeneficiaryOtpScreen', {
            state: {
              beneId: resp.data.beneficiary_id,
              bene_contactId: resp.data.contact_id,
              currency,
              transferflowamount,
              currencyid,
              balance: currency
                ? getAvailableBalance(currency).toLocaleString()
                : '-',
              nodeType: node_location,
              Nav_node,
              sendmoneynode,
              buyAmount,
              exchangeRate,
              sendmoney_contactId: contactId,
              from_currency,
              from_amount,
              sendmoney_Nav_node,
              node,
              previousPage: '/BusinessAccount',
              transfer_fee:transfer_fee,
              service_fee:service_fee,
              total_amount:total_amount,
            },
          }));
        })
        .catch((err) => {
          console.log(err.response.data);
          toast.error(err.response.data.message);
          setLoading(false);
        });
    }
  };

  const getNigeriaBanks = async () => {
    try {
      const resp = await axios.get(
        'https://banking.fxmaster.co.uk/api/dytona/bank-branches/?country=nigeria',
        {
          auth: {
            username: 'banking@fxmaster.co.uk',
            password: 'xHGmEkVpFDMZJoir',
          },
        },
      );
      setBanks(resp.data || []);
      setSelectedBank('');
    } catch (err) {
      console.log(err);
    }
  };

  const getData = async () => {
    const countriesList = JSON.parse(sessionStorage.getItem('countries')) || [];
    setCountries([countriesList]);
    if (Array.isArray(countriesList) && countriesList.length > 0) {
      if (sendmoney_Nav_node && currencyid) {
        const matched = countriesList.find((c) => c.id == currencyid);
        setbeneCountry(matched ? matched.code : '');
      } else {
        setbeneCountry('');
      }
    } else {
      setbeneCountry('');
    }
    if (sendmoney_Nav_node) setbeneType('regular');
  };

  const getBeneFields = async (type, code) => {
    setLoading(true);
    if (type == '' || type == null) type = 'regular';
    const token = sessionStorage.getItem('login_token');
    await axios
      .post(
        Constants.BASE_URL + 'API-FX-209-BENEFICIARY-FORM-FEILDS',
        {
          currency,
          payment_type: type,
          bank_country:
            code == '' || code == null
              ? beneCountry == 'UK'
                ? 'GB'
                : beneCountry
              : code == 'UK'
                ? 'GB'
                : code,
          beneficiary_type: 'company',
        },
        {
          headers: {
            fx_key: Constants.SUBSCRIPTION_KEY,
            Authorization: 'Bearer ' + JSON.parse(token),
          },
        },
      )
      .then((resp) => {
        setFormFeilds(Object.values(resp.data.form_fields));
        setFormKeys(Object.keys(resp.data.form_fields));
        setLoading(false);
      })
      .catch((err) => {
        console.log(err.response);
        setLoading(false);
      });
  };

  const searchBankData = async () => {
    if (currencyid != 231 && currencyid != 105) {
      if (bankDetails.iban != '' && bankDetails.iban != null)
        searchBank(beneCountry, bankDetails.iban, 'iban');
      else if (bankDetails.bic_swift != '' && bankDetails.bic_swift != null)
        searchBank(beneCountry, bankDetails.bic_swift, 'bank_code');
      else if (bankDetails.aba != '' && bankDetails.aba != null)
        searchBank(beneCountry, bankDetails.aba, 'bank_code');
      else if (bankDetails.branch_code != '' && bankDetails.branch_code != null)
        searchBank(beneCountry, bankDetails.branch_code, 'bank_code');
      else if (bankDetails.bsb_code != '' && bankDetails.bsb_code != null)
        searchBank(beneCountry, bankDetails.bsb_code, 'bank_code');
      else if (
        bankDetails.routing_code != '' &&
        bankDetails.routing_code != null
      )
        searchBank(beneCountry, bankDetails.routing_code, 'bank_code');
    }
  };

  const searchBank = async (country_code, query, search_type) => {
    setLoading(true);
    setBanks([]);
    try {
      const encryptedPayload = await encryptPayload({ country_code, query, search_type, platform: 'web', extraParamValidation: 'trustedfromweb' });
      const resp = await axios.post(
        Constants.BASE_URL + 'API-FX-243-SEARCH-BANK',
        encryptedPayload,
        { headers: { fx_key: Constants.SUBSCRIPTION_KEY } },
      );
      const uniqueBanks = Array.isArray(resp.data)
        ? resp.data.filter(
            (value, index, self) =>
              index ===
              self.findIndex(
                (t) =>
                  t.bankName === value.bankName &&
                  t.swiftBicCode === value.swiftBicCode &&
                  t.bankAddressLine1 === value.bankAddressLine1,
              ),
          )
        : [];
      setBanks(uniqueBanks);
      setSelectedBank(uniqueBanks.length === 0 ? 'other' : '');
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const closeAllDropdowns = () => {
    if (showdropdown) setShowdropdown(false);
    if (showPaymentDropdown) setShowPaymentDropdown(false);
  };

  const paymentTypeLabel =
    beneType === 'regular'
      ? 'Regular'
      : beneType === 'priority'
        ? 'Priority'
        : 'Select Priority Level';

  const PaymentTypeDropdown = ({ onSelect, disabled }) => (
    <div style={{ ...S.selectWrap, position: 'relative' }}>
      <div
        style={{
          ...S.selectTrigger,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          color: beneType ? '#111827' : '#9CA3AF',
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          setShowPaymentDropdown(!showPaymentDropdown);
          setShowdropdown(false);
        }}
      >
        {paymentTypeLabel}
      </div>
      <span style={S.chevronWrap}>
        <ChevronDown />
      </span>

      {showPaymentDropdown && (
        <div style={{ ...S.dropdown, maxHeight: '120px' }}>
          <div style={S.dropdownList}>
            {[
              { label: 'Regular', value: 'regular' },
              { label: 'Priority', value: 'priority' },
            ].map((opt) => (
              <div
                key={opt.value}
                className="ba-dropdown-item"
                style={{
                  ...S.dropdownItem,
                  background:
                    beneType === opt.value ? '#FFF9F5' : 'transparent',
                  fontWeight: beneType === opt.value ? 500 : 400,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setErrors({});
                  setbankDetails({});
                  setBanks([]);
                  setSelectedBank('');
                  setOtherBank('');
                  setbeneType(opt.value);
                  setShowPaymentDropdown(false);
                  if (onSelect) onSelect(opt.value);
                }}
              >
                {opt.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
  PaymentTypeDropdown.propTypes = {
    onSelect: PropTypes.func,
    disabled: PropTypes.bool,
  };

  const isInr = currency && currency.toLowerCase() === 'inr';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        @keyframes ba-spin { to { transform: rotate(360deg); } }
        @keyframes ba-fadein { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
        .ba-input:focus { border-color: #E8863A !important; box-shadow: 0 0 0 3px rgba(232,134,58,0.1) !important; }
        .ba-dropdown-item:hover { background: #FFF9F5; }
        .ba-field-in { animation: ba-fadein 0.18s ease both; }
        .ba-proceed-btn:hover { opacity: 0.93; transform: translateY(-1px); }
        .ba-proceed-btn:active { transform: scale(0.99); }
        .ba-back-btn:hover { color: #111827 !important; }
      `}</style>

      <div style={S.root} onClick={closeAllDropdowns}>
        <div style={S.pageWrap}>
          {/* ── Header — outside the card ── */}
          <div style={S.header}>
            <h1 style={S.pageTitle}>Business Account</h1>
            <p style={S.pageSubtitle}>Enter Account Details</p>
          </div>

          <div className="backDiv">
            <button
              className="ba-back-btn"
              style={S.backLink}
              onClick={() => {
                setErrors({});
                navigate('/BeneficiaryTypes', {
                  state: {
                    currency,
                    currencyid,
                    transferflowamount,
                    balance,
                    routeName,
                    nodeType: node_location || '',
                    node,
                    Nav_node,
                    sendmoneynode,
                    buyAmount,
                    exchangeRate,
                    contactId,
                    from_currency,
                    from_amount,
                    sendmoney_Nav_node,
                    sbnode,
                  },
                });
              }}
            >
              <ArrowLeft className='w-5 h-5 -mr-1.5' />
              Back
            </button>
          </div>

          {/* ── Card — body only ── */}
          <div
            style={S.card}
            onClick={(e) => {
              e.stopPropagation();
              closeAllDropdowns();
            }}
          >
            <ToastContainer />

            {/* ── Body ── */}
            <div style={S.body}>
              {/* INR: Payment Method Toggle */}
              {isInr && (
                <div style={S.radioToggleWrap}>
                  <div
                    style={S.radioOption}
                    onClick={() => {
                      setErrors({});
                      setbankDetails({});
                      setInrPaymentType('account_number');
                    }}
                  >
                    <div style={S.radioCircle}>
                      {inrPaymentType === 'account_number' && (
                        <div style={S.radioInner} />
                      )}
                    </div>
                    Account Number
                  </div>

                  <div
                    style={S.radioOption}
                    onClick={() => {
                      setErrors({});
                      setbankDetails({});
                      setInrPaymentType('upi');
                    }}
                  >
                    <div style={S.radioCircle}>
                      {inrPaymentType === 'upi' && <div style={S.radioInner} />}
                    </div>
                    UPI
                  </div>
                </div>
              )}

              {/* Non-INR: Country & Payment Type section */}
              {!isInr && countries.length > 0 && currencyid !== 105 && (
                <>
                  <div style={S.sectionHeader}>Country &amp; Payment Type</div>

                  {/* ── Country selector ── */}
                  <div style={S.selectWrap}>
                    <div
                      style={{
                        ...S.selectTrigger,
                        cursor: isFromSendMoneyFlow ? 'not-allowed' : 'pointer',
                        color: beneCountry ? '#111827' : '#9CA3AF',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isFromSendMoneyFlow) return;
                        setShowdropdown(!showdropdown);
                        setShowPaymentDropdown(false);
                        setSearchtext('');
                      }}
                    >
                      {beneCountry
                        ? countries[0].find((c) => c.code === beneCountry)
                            ?.name || 'Select Country'
                        : 'Select Country'}
                    </div>
                    <span style={S.chevronWrap}>
                      <ChevronDown />
                    </span>

                    {showdropdown && (
                      <div style={S.dropdown}>
                        <div style={S.dropdownSearch}>
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <circle
                              cx="6.5"
                              cy="6.5"
                              r="4.5"
                              stroke="#9CA3AF"
                              strokeWidth="1.6"
                            />
                            <path
                              d="M10 10l3 3"
                              stroke="#9CA3AF"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                            />
                          </svg>
                          <input
                            style={S.dropdownSearchInput}
                            type="text"
                            placeholder="Search country..."
                            autoFocus
                            value={searchtext}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setSearchtext(e.target.value)}
                          />
                        </div>
                        <div style={S.dropdownList}>
                          {filteredcountries.length > 0 ? (
                            filteredcountries.map((resp, index) => (
                              <div
                                key={index}
                                className="ba-dropdown-item"
                                style={S.dropdownItem}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setErrors({});
                                  setbankDetails({});
                                  setOtherBank('');
                                  setbeneCountry(resp.code);
                                  setBanks([]);
                                  setSelectedBank('');
                                  setOtherBankError('');
                                  setShowdropdown(false);
                                  setTimeout(() => {
                                    searchBankData();
                                  }, 0);
                                }}
                              >
                                {resp.name}
                              </div>
                            ))
                          ) : (
                            <div
                              style={{ ...S.dropdownItem, color: '#9CA3AF' }}
                            >
                              No countries found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Payment Type ── */}
                  {currencyid !== 105 &&
                    (currencyid === 231 ? (
                      beneCountry !== 'UK' && beneCountry !== 'GB' ? (
                        <PaymentTypeDropdown  />
                      ) : null
                    ) : (
                      <PaymentTypeDropdown  />
                    ))}
                </>
              )}

              {/* Account Details section */}
              {formFeilds.length > 0 && (
                <div
                  style={{
                    ...S.sectionHeader,
                    marginTop: isInr ? '4px' : '20px',
                  }}
                >
                  Account Details
                </div>
              )}

              {/* Form fields */}
              {formFeilds.length > 0 &&
                formFeilds.map((field, index) => {
                  const key = formKeys[index];
                  if (isInr && inrPaymentType === 'upi' && key === 'ifsc')
                    return null;
                  if (key === 'beneficiary_address') {
                    return (
                      <div
                        className="ba-field-in"
                        style={S.inputWrap}
                        key={index}
                      >
                        <AddressSearchInput
                          className="ba-input"
                          required={field.is_required}
                          placeholder="Address"
                          value={bankDetails[key]}
                          style={{
                            ...S.input,
                            ...(errors[key] ? S.inputError : {}),
                          }}
                          onChange={(text) => {
                            setbankDetails({ ...bankDetails, [key]: text });
                            if (errors[key]) setErrors({ ...errors, [key]: '' });
                          }}
                          onPlaceSelected={(place) => {
                            // Only fill City / PostCode when this currency's form shows them
                            const hasCity = formKeys.includes('beneficiary_city');
                            const hasPostcode = formKeys.includes('beneficiary_postcode');
                            const filled = {};
                            // Replace City / PostCode; ones Google lacks are cleared so old values don't mix in
                            if (hasCity) filled.beneficiary_city = place.city;
                            if (hasPostcode)
                              // PostCode field only allows letters and numbers
                              filled.beneficiary_postcode = place.postcode.replace(/[^a-zA-Z0-9]/g, '');
                            // Street only when City and PostCode have their own fields, so nothing is lost
                            if (hasCity && hasPostcode && place.addressLine1)
                              filled.beneficiary_address = place.addressLine1;
                            setbankDetails((prev) => ({ ...prev, ...filled }));
                            setErrors((prev) => ({
                              ...prev,
                              ...Object.keys(filled).reduce((acc, k) => ({ ...acc, [k]: '' }), {}),
                            }));
                          }}
                        />
                        {errors[key] && <p style={S.errorText}>{errors[key]}</p>}
                      </div>
                    );
                  }

                  return (
                    <div
                      className="ba-field-in"
                      style={S.inputWrap}
                      key={index}
                    >
                      <input
                        className="ba-input"
                        type={field.type}
                        required={field.is_required}
                        placeholder={
                          key === 'account_number'
                            ? inrPaymentType === 'upi'
                              ? 'UPI ID'
                              : 'Account Number'
                            : key === 'beneficiary_company_name'
                              ? 'Company Name'
                              : key === 'beneficiary_city'
                                ? 'City'
                                : key === 'beneficiary_address'
                                  ? 'Address'
                                  : key === 'beneficiary_postcode'
                                    ? 'PostCode'
                                    : key === 'bic_swift'
                                      ? 'BIC / SWIFT'
                                      : key === 'iban'
                                        ? 'IBAN'
                                        : key === 'beneficiary_first_name'
                                          ? 'First Name'
                                          : key === 'beneficiary_last_name'
                                            ? 'Last Name'
                                            : field.label +
                                              (field.is_required ? '' : '')
                        }
                        value={bankDetails[key] || ''}
                        style={{
                          ...S.input,
                          ...(errors[key] ? S.inputError : {}),
                        }}
                        onBlur={() => {
                          const validKeys = [
                            'iban',
                            'bic_swift',
                            'aba',
                            'branch_code',
                            'bsb_code',
                            'routing_code',
                          ];
                          if (
                            validKeys.includes(key) &&
                            bankDetails[key]?.trim()
                          )
                            searchBankData();
                        }}
                        onChange={(e) => {
                          // PostCode allows only letters and numbers
                          const value =
                            key === 'beneficiary_postcode'
                              ? e.target.value.replace(/[^a-zA-Z0-9]/g, '')
                              : e.target.value;
                          setbankDetails({
                            ...bankDetails,
                            [key]: value,
                          });
                          if (errors[key]) setErrors({ ...errors, [key]: '' });
                          if (
                            [
                              'iban',
                              'bic_swift',
                              'aba',
                              'branch_code',
                              'bsb_code',
                              'routing_code',
                            ].includes(key) &&
                            e.target.value.trim() === ''
                          ) {
                            setBanks([]);
                            setSelectedBank('');
                          }
                        }}
                      />
                      {errors[key] && <p style={S.errorText}>{errors[key]}</p>}
                    </div>
                  );
                })}

              {/* Bank selector */}
              {beneCountry !== 'NG' &&
                bank.length > 0 &&
                Object.keys(bankDetails).length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ ...S.sectionHeader, marginTop: '16px' }}>
                      Select Your Bank
                    </div>
                    <div style={S.selectWrap}>
                      <select
                        className="ba-select"
                        style={S.bankSelect}
                        onChange={(e) => {
                          setSelectedBank(e.target.value);
                          setBankError('');
                        }}
                      >
                        <option value="">— Please Select Your Bank —</option>
                        {bank.map((b, index) => (
                          <option key={index} value={JSON.stringify(b)}>
                            {b.bankName} — {b.bankAddressLine1}
                          </option>
                        ))}
                        <option value="other">Other</option>
                      </select>
                      <span style={S.chevronWrap}>
                        <ChevronDown />
                      </span>
                    </div>
                    {bankError && <p style={S.errorText}>{bankError}</p>}
                  </div>
                )}

              {/* Other bank address */}
              {selectedBank === 'other' &&
                Object.keys(bankDetails).length > 0 && (
                  <div style={S.inputWrap} className="ba-field-in">
                    <input
                      className="ba-input"
                      type="text"
                      required
                      placeholder="Enter Your Bank Address"
                      value={otherBank || ''}
                      style={{
                        ...S.input,
                        ...(otherBankError ? S.inputError : {}),
                      }}
                      onChange={(e) => {
                        setOtherBank(e.target.value);
                        setOtherBankError('');
                      }}
                      onBlur={() => {
                        if (!otherBank?.trim())
                          setOtherBankError('Bank address is required');
                        else setOtherBankError('');
                      }}
                    />
                    {otherBankError && (
                      <p style={S.errorText}>{otherBankError}</p>
                    )}
                  </div>
                )}
            </div>
                        {beneCountry === 'NG' && bank.length > 0 && (
              <div style={{ marginTop: '16px', marginBottom: '10px',marginLeft:'20px',marginRight:'20px' }}>
                {/* <div style={{ ...S.sectionHeader, marginTop: '0px' }}>
                  Select Your Bank
                </div> */}

                <div style={S.selectWrap}>
                  <select
                    className="sa-select bg-transparent"
                    value={selectedBank || ''}
                    style={{
                      ...S.bankSelect,
                      ...(bankError ? S.inputError : {}),
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      backgroundColor: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '14px',
                      padding: '14px 44px 14px 16px',
                      fontSize: '15px',
                      color: '#111827',
                      width: '100%',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                    }}
                    onChange={(e) => {
                      setSelectedBank(e.target.value);
                      setBankError('');
                    }}
                  >
                    <option value="">— Please Select Your Bank —</option>

                    {bank.map((b, idx) => (
                      <option key={idx} value={JSON.stringify(b)}>
                        {b.bank_name}
                      </option>
                    ))}
                  </select>

                  <span style={S.chevronWrap}>
                    <ChevronDown />
                  </span>
                </div>

                {bankError && <p style={S.errorText}>{bankError}</p>}
              </div>
            )}
          </div>

          {/* Loading / Continue button */}
          {loading ? (
            <div style={S.loadingWrap}>
              <Spinner />
              <span>Processing…</span>
            </div>
          ) : (
            formFeilds.length > 0 && (
              <button
                className="ba-proceed-btn"
                style={S.proceedBtn}
                onClick={submitHandler}
              >
                <span>Continue</span>
                <ArrowRight className='w-5 h-5 -ml-2.5' />
              </button>
            )
          )}
        </div>
      </div>
    </>
  );
}

export default BusinessAccount;